import {create} from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware'; // Usamos createJSONStorage
import axiosInstance from './AxiosInstance'; // Instancia de axios con configuración personalizada
import { toast } from 'react-hot-toast';

// Definir tipos de datos
interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  // Agregar otros campos según sea necesario
}

interface AuthStore {
  isAuthenticated: boolean;
  user: User | null;
  message: string;
  loading: boolean;
  verifyAuth: () => Promise<void>;
  login: (loginData: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  checkRole: (role: string) => boolean;
}

// Store de autenticación
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      isAuthenticated: false, // Estado para saber si el usuario está autenticado
      user: null, // Datos del usuario autenticado (sin información sensible)
      message: '', // Mensaje de error o éxito
      loading: false, // Estado de carga para evitar múltiples solicitudes simultáneas

      // Verificar la autenticación al cargar la aplicación
      verifyAuth: async () => {
        set({ loading: true });
        try {
          const response = await axiosInstance.get('/verify-auth', { withCredentials: true });
          const { authenticated, user, message } = response.data;

          if (authenticated) {
            set({ isAuthenticated: true, user, message, loading: false });
          } else {
            set({ isAuthenticated: false, user: null, message, loading: false });
          }
        } catch (error) {
          set({ isAuthenticated: false, user: null, message: 'Error al verificar autenticación', loading: false });
          toast.error('Error al verificar autenticación');
        }
      },

      // Función para iniciar sesión
      login: async (loginData: { email: string; password: string }) => {
        set({ loading: true });
        try {
          const response = await axiosInstance.post('/pixel-users/login', loginData, { withCredentials: true });

          if (response.data.user) {
            const { user } = response.data;
            set({
              isAuthenticated: true,
              user: {
                ...user, // Asegurarse de incluir todos los detalles del usuario, como el rol
                role: user.role || 'user', // Asignar rol por defecto si no existe
              },
              message: 'Login exitoso',
              loading: false,
            });
            toast.success('¡Bienvenido!');
          } else {
            set({ isAuthenticated: false, user: null, message: 'Credenciales incorrectas', loading: false });          }
        } catch (error) {
          set({ isAuthenticated: false, user: null, message: error.response?.data?.message || 'Error al iniciar sesión', loading: false });
          toast.error(error.response?.data?.message || 'Credenciales incorrectas');
        }
      },

      // Función para cerrar sesión
      logout: async () => {
        set({ loading: true });
        try {
          await axiosInstance.post('/pixel-users/logout', {}, { withCredentials: true });
          set({ isAuthenticated: false, user: null, message: '', loading: false });
          toast.success('¡Has cerrado sesión!');
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          set({ loading: false });
          toast.error('Error al cerrar sesión');
        }
      },

      // Verificar el rol del usuario
      checkRole: (role: string) => {
        const { user } = get();
        return user?.role === role; // Verificar si el usuario tiene el rol solicitado
      },
    }),
    {
      name: 'auth-store',
      storage: createJSONStorage(() => localStorage), // Usar createJSONStorage para persistir con localStorage
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user, // No guardar contraseñas ni datos sensibles
        message: state.message,  // No omitir mensaje
        loading: state.loading,  // No omitir loading
        verifyAuth: state.verifyAuth, // Mantener las funciones dentro del store
        login: state.login, // Mantener las funciones dentro del store
        logout: state.logout, // Mantener las funciones dentro del store
        checkRole: state.checkRole, // Mantener las funciones dentro del store
      }),
    }
  )
);
